import { CapacitorDownloader } from "@capgo/capacitor-downloader";

// The batch methods are ours — added to the plugin by the SFC patch (see
// patches/README.md), so upstream's TypeScript API doesn't declare them. Typed
// here rather than at the call site, so the shape the Swift side actually
// resolves is written down in exactly one place.
//
// The Swift parses these keys by string (`entry["id"]`, `entry["url"]`, ...),
// as does the rest of the JS↔native contract — renaming one means editing both
// sides together. See src/client/iphone/CLAUDE.md.
export type BatchDownloadRequest = {
  id: string;
  url: string;
  destination: string;
  priority?: "high" | "low";
};

export type BatchDownloadResult = {
  id: string;
  /** Present only on a per-item failure; `state` is absent when it is. */
  error?: string;
  /** RUNNING = took a native transfer slot, PENDING = waiting for one. */
  state?: string;
  progress?: number;
};

type BatchCapableDownloader = {
  downloadBatch: (options: {
    downloads: BatchDownloadRequest[];
  }) => Promise<{ results: BatchDownloadResult[] }>;
  deleteFiles: (options: { paths: string[] }) => Promise<{ failed: string[] }>;
  stopBatch: (options: { ids: string[] }) => Promise<{ stopped: string[] }>;
};

// Whether the installed plugin actually implements each batch method.
//
// This CANNOT be answered by looking. A Capacitor plugin object is a Proxy
// whose `get` trap synthesizes a method wrapper for every property name, so
// `typeof plugin.downloadBatch === "function"` is true on every platform,
// patched or not (see @capacitor/core's registerPlugin, `default:` branch).
// A probe like that reports support, the call then rejects UNIMPLEMENTED, and
// the caller treats a whole round as failed instead of falling back — turning
// a dropped patch from "slow" into "downloads don't work".
//
// So support is learned from the first real call and remembered; only the
// first chunk of a session pays the failed call.
const supported = new Map<keyof BatchCapableDownloader, boolean>();

// Capacitor rejects a missing native method with ExceptionCode.UNIMPLEMENTED,
// and the web/no-op path with the same code. Anything else — a real failure
// inside a method that DOES exist — must propagate, or one transient native
// error would silently downgrade the whole session to the per-item path.
function isUnimplemented(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  if ((error as { code?: unknown }).code === "UNIMPLEMENTED") {
    return true;
  }

  const message = (error as { message?: unknown }).message;

  return typeof message === "string" && message.includes("not implemented");
}

async function callBatchMethod<K extends keyof BatchCapableDownloader>(
  method: K,
  options: Parameters<BatchCapableDownloader[K]>[0],
): Promise<Awaited<ReturnType<BatchCapableDownloader[K]>> | null> {
  if (supported.get(method) === false) {
    return null;
  }

  const plugin = CapacitorDownloader as unknown as BatchCapableDownloader;

  // A plain (non-proxy) plugin object — the web stub, or a test double — can
  // simply lack the property. That is unimplemented too, and calling it would
  // throw a TypeError the rejection check below would not recognize. Absence
  // is never conclusive on the native proxy, which answers everything; the
  // rejection path covers that case.
  if (typeof plugin[method] !== "function") {
    supported.set(method, false);

    return null;
  }

  try {
    const result = await (plugin[method] as (o: unknown) => Promise<unknown>)(
      options,
    );
    supported.set(method, true);

    return result as Awaited<ReturnType<BatchCapableDownloader[K]>>;
  } catch (error) {
    if (!isUnimplemented(error)) {
      throw error;
    }

    // Patch dropped by a version bump, or the web build. Remember it, so the
    // rest of the session goes straight to the per-item path.
    supported.set(method, false);

    return null;
  }
}

/**
 * Create every task in `downloads` with ONE bridge call. Returns null when the
 * installed plugin has no batch method, so the caller falls back to the
 * per-item path rather than failing the queue: slower, but a round that
 * downloads slowly beats a round that does not download.
 */
export async function downloadBatch(
  downloads: BatchDownloadRequest[],
): Promise<BatchDownloadResult[] | null> {
  const result = await callBatchMethod("downloadBatch", { downloads });

  return result?.results ?? null;
}

/**
 * Delete many downloaded files with ONE bridge call. Paths are relative to
 * Documents. Returns null when the installed plugin lacks the method, so the
 * caller can fall back to per-file deletes.
 */
export async function deleteFilesBatch(
  paths: string[],
): Promise<{ failed: string[] } | null> {
  return callBatchMethod("deleteFiles", { paths });
}

/**
 * Cancel many native tasks with ONE bridge call. Ids with no task are skipped,
 * not errors — the caller is cancelling a whole round and does not know which
 * ones the session still holds. Returns null when the plugin lacks the method.
 */
export async function stopBatch(
  ids: string[],
): Promise<{ stopped: string[] } | null> {
  return callBatchMethod("stopBatch", { ids });
}

/** Test seam: forget what earlier calls learned about plugin support. */
export function resetBatchSupportCache(): void {
  supported.clear();
}
